import { filterSupersededShortTermConditionLayers } from 'features/dataLayers/canonicalShortTermConditionLayers'
import { paramId } from 'features/dataLayers/cdfParamValue'
import { SHORT_TERM_FEATURE_ALGORITHM_VERSION } from 'features/dataLayers/generation/shortTermCorrosionRate/constants'
import {
  getCdfResolutionKey,
  getComparableShortTermRateInspectionCdfGroups,
  getInspectionCdfsForResolutionKey,
  getShortTermRateInspectionCdfGroups,
  type ShortTermRateInspectionChoice,
} from 'features/dataLayers/generation/shortTermCorrosionRate/inspectionChoices'
import {
  type RolodexEntryQueryBody,
  useRolodexEntriesWithSyncedRefreshes,
} from 'features/rolodex'
import { useMemo } from 'react'
import {
  GeckoAssetModel,
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoInspection,
  GeckoView,
  isInspectionThicknessCDF,
  RolodexEntryType,
} from 'types'

export type ShortTermCorrosionWorkflowPair = {
  component: GeckoComponent
  target: ShortTermRateInspectionChoice
  comparison: ShortTermRateInspectionChoice
  resultCdf?: GeckoComponentDataFrame
}

export type ShortTermCorrosionWorkflowModel = {
  componentDataFrames: readonly GeckoComponentDataFrame[]
  generatedResultCdfs: readonly GeckoComponentDataFrame[]
  defaultPair?: ShortTermCorrosionWorkflowPair
  isLoading: boolean
  refresh: () => Promise<void>
}

export type ShortTermCorrosionWorkflowReferenceData = {
  assetModel?: GeckoAssetModel
  components: readonly GeckoComponent[]
  inspections: readonly GeckoInspection[]
  unitID?: string | null
}

export function useShortTermCorrosionWorkflowModel(
  referenceData: ShortTermCorrosionWorkflowReferenceData,
  preferredTargetInspectionId?: string | null,
  generatedResultCdfs: readonly GeckoComponentDataFrame[] = []
): ShortTermCorrosionWorkflowModel {
  const query = useMemo(
    () =>
      buildShortTermCorrosionWorkflowQueries({
        unitID: referenceData.unitID,
      }),
    [referenceData.unitID]
  )

  const { componentDataFrames, isLoading, forceRefresh } =
    useRolodexEntriesWithSyncedRefreshes(query)
  const visibleComponentDataFrames = useMemo(
    () => filterSupersededShortTermConditionLayers(componentDataFrames),
    [componentDataFrames]
  )

  const defaultPair = useMemo(
    () =>
      getDefaultShortTermCorrosionPair({
        components: referenceData.components,
        inspections: referenceData.inspections,
        componentDataFrames: visibleComponentDataFrames,
        generatedResultCdfs,
        preferredTargetInspectionId,
      }),
    [
      generatedResultCdfs,
      preferredTargetInspectionId,
      referenceData.components,
      referenceData.inspections,
      visibleComponentDataFrames,
    ]
  )

  return useMemo(
    () => ({
      componentDataFrames: visibleComponentDataFrames,
      generatedResultCdfs,
      defaultPair,
      isLoading,
      refresh: forceRefresh,
    }),
    [
      defaultPair,
      forceRefresh,
      generatedResultCdfs,
      isLoading,
      visibleComponentDataFrames,
    ]
  )
}

export function buildShortTermCorrosionWorkflowQueries(
  referenceData: Pick<ShortTermCorrosionWorkflowReferenceData, 'unitID'>
): RolodexEntryQueryBody[] | undefined {
  if (!referenceData.unitID) return undefined

  return [
    {
      type: [
        RolodexEntryType.COMPONENT_DATA_FRAME,
        RolodexEntryType.COMPONENT_DATASET,
      ],
      tags: { unit: [referenceData.unitID] },
      limit: 100000,
    },
  ]
}

export function getDefaultShortTermCorrosionPair({
  components,
  inspections,
  componentDataFrames,
  generatedResultCdfs = [],
  preferredTargetInspectionId,
}: {
  components: readonly GeckoComponent[]
  inspections: readonly GeckoInspection[]
  componentDataFrames: readonly GeckoComponentDataFrame[]
  generatedResultCdfs?: readonly GeckoComponentDataFrame[]
  preferredTargetInspectionId?: string | null
}): ShortTermCorrosionWorkflowPair | undefined {
  const resultCdfs = filterSupersededShortTermConditionLayers([
    ...generatedResultCdfs,
    ...componentDataFrames,
  ])

  for (const component of components) {
    const groups = getShortTermRateInspectionCdfGroups({
      componentId: component.id,
      componentDataFrames,
      inspections,
      templateCdf: undefined,
    })
    const preferredTargetGroup = groups.find(
      (group) => group.inspectionId === preferredTargetInspectionId
    )
    const targetGroups = preferredTargetGroup
      ? [
          preferredTargetGroup,
          ...groups.filter((group) => group !== preferredTargetGroup),
        ]
      : groups

    for (const targetGroup of targetGroups) {
      const targetCdf = targetGroup.cdfs.find(isInspectionThicknessCDF)
      if (!targetCdf) continue

      const comparisonGroup = getComparableShortTermRateInspectionCdfGroups({
        componentId: component.id,
        componentDataFrames,
        inspections,
        templateCdf: targetCdf,
      }).find(
        (group) =>
          group.inspection.data.date.getTime() <
          targetGroup.inspection.data.date.getTime()
      )
      if (!comparisonGroup) continue

      const comparisonCdf =
        getInspectionCdfsForResolutionKey(
          [comparisonGroup],
          getCdfResolutionKey(targetCdf)
        )[0]?.cdf ?? comparisonGroup.cdfs[0]
      if (!comparisonCdf) continue

      const target = {
        inspectionId: targetGroup.inspectionId,
        inspection: targetGroup.inspection,
        cdf: targetCdf,
        label: targetGroup.label,
      }
      const comparison = {
        inspectionId: comparisonGroup.inspectionId,
        inspection: comparisonGroup.inspection,
        cdf: comparisonCdf,
        label: comparisonGroup.label,
      }

      return {
        component,
        target,
        comparison,
        resultCdf: findSessionShortTermCorrosionCdf(
          resultCdfs,
          target.cdf,
          comparison.cdf
        ),
      }
    }
  }

  return undefined
}

export function findSessionShortTermCorrosionCdf(
  generatedResultCdfs: readonly GeckoComponentDataFrame[],
  targetCdf: GeckoComponentDataFrame | undefined,
  comparisonCdf: GeckoComponentDataFrame | undefined
): GeckoComponentDataFrame | undefined {
  if (!targetCdf || !comparisonCdf) return undefined

  return generatedResultCdfs
    .filter(isSessionShortTermCorrosionRateCdf)
    .filter(
      (cdf) =>
        cdf.tags.component === targetCdf.tags.component &&
        cdf.tags.inspection === targetCdf.tags.inspection &&
        paramId(dynamicParam(cdf, 'target_thickness_cdf_id')) ===
          targetCdf.id &&
        paramId(dynamicParam(cdf, 'comparison_thickness_cdf_id')) ===
          comparisonCdf.id &&
        paramId(dynamicParam(cdf, 'algorithm_version')) ===
          SHORT_TERM_FEATURE_ALGORITHM_VERSION
    )
    .toSorted((left, right) => right.generation - left.generation)[0]
}

export function isSessionShortTermCorrosionRateCdf(
  cdf: GeckoComponentDataFrame
): boolean {
  return paramId(cdf.data.params.view) === GeckoView.ShortTermCorrosionRate.id
}

function dynamicParam(
  cdf: GeckoComponentDataFrame,
  paramName: string
): unknown {
  return (cdf.data.params as unknown as Record<string, unknown>)[paramName]
}
